const ACTION = "FORM_SUBMIT";
const RECAPTCHA_KEY = process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY;

declare const grecaptcha: {
  enterprise?: {
    ready: (cb: () => void) => void;
    execute: (key: string, opts: { action: string }) => Promise<string>;
  };
};

let scriptLoaded = false;

/**
 * Injects the reCAPTCHA Enterprise script tag on demand (idempotent).
 * Called automatically by {@link validateRecaptcha} so the script is
 * only fetched when a form actually needs it — keeping third-party
 * cookies off pages that never submit a form.
 */
function ensureRecaptchaScript(): void {
  if (!RECAPTCHA_KEY || typeof document === "undefined") return;
  const id = "recaptcha-enterprise-script";
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_KEY}`;
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Start loading reCAPTCHA without blocking. Call this when a form mounts
 * or a dialog opens so the script is ready by the time the user submits.
 */
export const preloadRecaptcha = ensureRecaptchaScript;

function waitForRecaptcha(timeoutMs = 10_000): Promise<void> {
  ensureRecaptchaScript();
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (typeof grecaptcha !== "undefined" && grecaptcha.enterprise?.ready) {
        grecaptcha.enterprise.ready(() => {
          if (!scriptLoaded) {
            scriptLoaded = true;
            window.dispatchEvent(new Event("recaptcha:loaded"));
          }
          resolve();
        });
        return;
      }
      if (Date.now() - start > timeoutMs) {
        reject(new Error("reCAPTCHA failed to load"));
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  });
}

/** Returns a fresh token for APIs that verify it as part of the protected write. */
export async function getRecaptchaToken(): Promise<string> {
  if (!RECAPTCHA_KEY) return "";
  await waitForRecaptcha();
  return grecaptcha.enterprise!.execute(RECAPTCHA_KEY, { action: ACTION });
}

export async function validateRecaptcha() {
  if (!RECAPTCHA_KEY) return true;
  const token = await getRecaptchaToken();

  const response = await fetch("/api/validate-re-captcha", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, action: ACTION }),
  });
  const body = await response.json();
  const score = await body?.score;

  return score >= 0.5;
}
